import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ExternalLink, Phone } from "lucide-react";
import { api, formatRupiah, imageUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PAY_LABEL = {
  belum_upload: ["Belum Upload", "bg-[#EFE9DD] text-[#6B6250]"],
  menunggu_verifikasi: ["Menunggu Verifikasi", "bg-[#F6EBC8] text-[#6B5A14]"],
  terverifikasi: ["Terverifikasi", "bg-[#DCEBD9] text-[#2E4530]"],
  ditolak: ["Ditolak", "bg-[#F1D9D9] text-[#7A2E2E]"],
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "-");

export default function PaymentsAdmin() {
  const newOrders = useOutletContext();
  const [filter, setFilter] = useState("menunggu");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = (f = filter) => {
    setLoading(true);
    return api
      .get(`/payments?filter=${f}`)
      .then((r) => setOrders(r.data))
      .catch(() => toast.error("Gagal memuat pembayaran"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const verify = async (o) => {
    try {
      await api.post(`/payments/${o.id}/verify`);
      toast.success(`Pembayaran ${o.order_code} dikonfirmasi — status Lunas`);
      load();
      newOrders?.refresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengonfirmasi pembayaran");
    }
  };

  const reject = async () => {
    try {
      await api.post(`/payments/${rejecting.id}/reject`, { reason });
      toast.success(`Bukti transfer ${rejecting.order_code} ditolak`);
      setRejecting(null);
      setReason("");
      load();
      newOrders?.refresh?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Alasan minimal 3 karakter");
    }
  };

  return (
    <div data-testid="payments-admin">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-serif-display text-3xl font-bold text-[#1E2B1D]">Manajemen Pembayaran</h1>
          <p className="text-xs text-[#6B7C66] mt-1">Periksa bukti transfer dari pembeli, lalu konfirmasi atau tolak.</p>
        </div>
        <div className="flex gap-1 bg-[#EFE9DD] p-1 rounded-xl">
          {[["menunggu", "Menunggu Verifikasi"], ["semua", "Semua Bukti"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`payments-filter-${k}`}
              className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${filter === k ? "bg-[#3D5A40] text-[#FAF7EE]" : "text-[#3D5A40] hover:bg-[#F4EFE6]"}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p data-testid="payments-loading" className="text-sm text-[#6B7C66]">Memuat...</p>
      ) : orders.length === 0 ? (
        <p data-testid="payments-empty" className="text-sm text-[#6B7C66] bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-8 text-center">
          {filter === "menunggu" ? "Tidak ada bukti transfer yang menunggu verifikasi." : "Belum ada bukti transfer masuk."}
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {orders.map((o) => {
            const [label, cls] = PAY_LABEL[o.payment_status] || PAY_LABEL.belum_upload;
            return (
              <div key={o.id} data-testid={`payment-card-${o.id}`} className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-4 flex gap-4">
                <button
                  onClick={() => setPreview(o)}
                  data-testid={`payment-proof-thumb-${o.id}`}
                  className="w-28 h-36 rounded-xl overflow-hidden bg-[#EFE9DD] shrink-0 border border-[#E0D7C6] hover:opacity-90"
                  aria-label="Lihat bukti transfer"
                >
                  <img src={imageUrl(o.proof_url)} alt={`Bukti ${o.order_code}`} className="w-full h-full object-cover" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="font-mono font-bold text-[#3D5A40] text-sm">{o.order_code}</span>
                      <span className="block text-xs font-semibold text-[#1E2B1D] mt-0.5">{o.buyer_name}</span>
                    </div>
                    <span data-testid={`payment-status-${o.id}`} className={`text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap ${cls}`}>{label}</span>
                  </div>
                  <div className="mt-2 text-xs text-[#5E6D5B] space-y-0.5">
                    <p>Total: <span className="font-bold text-[#1E2B1D]">{formatRupiah(o.total)}</span></p>
                    <p>Diunggah: {fmt(o.proof_uploaded_at)}</p>
                    <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> {o.phone}</p>
                    {o.payment_status === "ditolak" && o.payment_note && (
                      <p className="text-[#7A2E2E]">Alasan: {o.payment_note}</p>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {o.payment_status !== "terverifikasi" && (
                      <button onClick={() => verify(o)} data-testid={`payment-verify-${o.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#3D5A40] hover:bg-[#2E4530] text-[#FAF7EE] text-xs font-bold transition-colors">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Konfirmasi
                      </button>
                    )}
                    {o.payment_status === "menunggu_verifikasi" && (
                      <button onClick={() => setRejecting(o)} data-testid={`payment-reject-${o.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#C97B7B] text-[#7A2E2E] hover:bg-red-50 text-xs font-bold transition-colors">
                        <XCircle className="w-3.5 h-3.5" /> Tolak
                      </button>
                    )}
                    <a
                      href={`https://wa.me/${o.phone.replace(/\D/g, "").replace(/^0/, "62")}?text=${encodeURIComponent(`Halo ${o.buyer_name}, terkait pesanan ${o.order_code} di Puspita Florist: `)}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`payment-wa-${o.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#D5CCBC] text-[#3D5A40] hover:bg-[#F4EFE6] text-xs font-semibold transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> Hubungi
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-2xl bg-[#FBF9F5]" data-testid="payment-proof-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl">Bukti Transfer {preview?.order_code}</DialogTitle>
            <DialogDescription className="sr-only">Pratinjau bukti transfer pembeli</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <img src={imageUrl(preview.proof_url)} alt="Bukti transfer" className="w-full max-h-[70vh] object-contain rounded-xl bg-[#EFE9DD]" />
              <p className="text-xs text-[#5E6D5B]">
                {preview.buyer_name} · {formatRupiah(preview.total)} · {fmt(preview.proof_uploaded_at)}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent className="max-w-md bg-[#FBF9F5]" data-testid="payment-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl">Tolak Bukti Transfer</DialogTitle>
            <DialogDescription className="text-xs text-[#5E6D5B]">Tuliskan alasan penolakan untuk pesanan {rejecting?.order_code}. Sampaikan ke pembeli lewat WhatsApp agar mereka mengunggah ulang.</DialogDescription>
          </DialogHeader>
          <textarea
            rows="3"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="payment-reject-reason-input"
            placeholder="Contoh: nominal transfer tidak sesuai / bukti tidak terbaca"
            className="w-full px-3 py-2 rounded-lg border border-[#D5CCBC] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3D5A40]"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setRejecting(null)} className="px-4 py-2 rounded-lg border border-[#D5CCBC] text-xs font-semibold text-[#5E6D5B]">Batal</button>
            <button onClick={reject} data-testid="payment-reject-confirm-btn" className="px-4 py-2 rounded-lg bg-[#7A2E2E] hover:bg-[#5E2222] text-white text-xs font-bold">Tolak Bukti</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
